import { ConflictAlertSeverityBadge } from '../badges/ConflictAlertSeverityBadge';
import { ConflictAlertTypeBadge } from '../badges/ConflictAlertTypeBadge';

import type { PriorityQueueVM } from '../../view-models';
import type React from 'react';

export interface PriorityQueueViewProps {
  pq: PriorityQueueVM;
  onNavigate?: (target: string) => void;
  /** Abre a conversa do item (gate core-conversation na page); ausente = item não navega. */
  onOpenConversation?: (conversationId: string) => void;
}

export function PriorityQueueView({
  pq,
  onNavigate,
  onOpenConversation,
}: PriorityQueueViewProps): React.JSX.Element {
  return (
    <aside className="priority-col">
      <div className="tela1-col__title">Conversas priorizadas</div>

      {(pq.conflictAlerts?.length ?? 0) > 0 && (
        <section className="priority-col__group">
          <div className="priority-col__group-head priority-col__group-head--danger">
            <span>🚨 ConflictAlerts ({pq.conflictAlerts!.length})</span>
            <button
              type="button"
              className="btn-ghost-mini"
              onClick={() => onNavigate?.('centro-alertas')}
            >
              Ver Centro
            </button>
          </div>
          {pq.conflictAlerts!.map((a, i) => (
            <div key={i} className="priority-col__item">
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <ConflictAlertSeverityBadge severity={a.severity} />
                <span className="priority-col__item-title">{a.summary}</span>
              </div>
              <div className="priority-col__item-meta">
                <ConflictAlertTypeBadge alertType={a.alertType} />
                {' · '}chain {a.chainPos ?? '1/1'} · {a.detectedAt}
              </div>
            </div>
          ))}
        </section>
      )}

      {(pq.slaEstourado?.length ?? 0) > 0 && (
        <section className="priority-col__group">
          <div className="priority-col__group-head">
            <span>⚠ SLA estourado ({pq.slaEstourado!.length})</span>
          </div>
          {pq.slaEstourado!.map((s, i) => (
            <div key={i} className="priority-col__item">
              <div className="priority-col__item-title">
                {s.name ?? 'Conversa sem identificação'}
              </div>
              <div className="priority-col__item-meta">
                aguarda {s.waiting}
                {onOpenConversation !== undefined && s.conversationId !== undefined && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="btn-ghost-mini"
                      onClick={() => onOpenConversation(s.conversationId!)}
                    >
                      Abrir conversa
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      {(pq.pendencyOverdue?.length ?? 0) > 0 && (
        <section className="priority-col__group">
          <div className="priority-col__group-head priority-col__group-head--danger">
            <span>🔴 Pendências &gt; 4h ({pq.pendencyOverdue!.length})</span>
          </div>
          {pq.pendencyOverdue!.map((p, i) => (
            <div key={i} className="priority-col__item">
              <div className="priority-col__item-title">{p.title}</div>
              <div className="priority-col__item-meta">
                {p.operator} · vencida há {p.overdue}
                {onOpenConversation !== undefined && p.conversationId !== undefined && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="btn-ghost-mini"
                      onClick={() => onOpenConversation(p.conversationId!)}
                    >
                      Abrir conversa
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      {(pq.promisesBroken?.length ?? 0) > 0 && (
        <section className="priority-col__group">
          <div className="priority-col__group-head priority-col__group-head--danger">
            <span>🤝 Promessas quebradas ({pq.promisesBroken!.length})</span>
          </div>
          {pq.promisesBroken!.map((p, i) => (
            <div key={i} className="priority-col__item">
              <div className="priority-col__item-title">{p.promise}</div>
              <div className="priority-col__item-meta">
                {p.person} · {p.operator} · vencida há {p.overdue}
                {onOpenConversation !== undefined && p.conversationId !== undefined && (
                  <>
                    {' · '}
                    <button
                      type="button"
                      className="btn-ghost-mini"
                      onClick={() => onOpenConversation(p.conversationId!)}
                    >
                      Abrir conversa
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </section>
      )}

      {(pq.commitmentsBroken?.length ?? 0) > 0 && (
        <section className="priority-col__group">
          <div className="priority-col__group-head">
            <span>💔 Compromissos quebrados ({pq.commitmentsBroken!.length})</span>
          </div>
          {pq.commitmentsBroken!.map((c, i) => (
            <div key={i} className="priority-col__item">
              <div className="priority-col__item-title">{c.person}</div>
              <div className="priority-col__item-meta">{c.commitment}</div>
            </div>
          ))}
        </section>
      )}

      {(pq.noShows?.length ?? 0) > 0 && (
        <section className="priority-col__group">
          <div className="priority-col__group-head">
            <span>❌ Faltas recentes ({pq.noShows!.length})</span>
          </div>
          {pq.noShows!.slice(0, 3).map((n, i) => (
            <div key={i} className="priority-col__item">
              <div className="priority-col__item-title">{n.person}</div>
              <div className="priority-col__item-meta">
                {n.when} · {n.service}
              </div>
            </div>
          ))}
        </section>
      )}
    </aside>
  );
}
